// src/context/brandVoice/useLeaveGuard.js — "you have unsaved changes" while `dirty`.
//
// ⚠️ The app runs on <BrowserRouter> (react-router 6.30), where useBlocker /
// unstable_usePrompt do NOT work (they need a data router), and migrating the app is
// out of scope. So the guard covers the two paths a user actually takes:
//   • closing / reloading the browser tab → `beforeunload` (the browser's own prompt);
//   • clicking an in-app LINK (sidebar, avatar menu, any <Link>) → a capture-phase
//     document listener asks window.confirm first; cancelling calls preventDefault,
//     which react-router's <Link> honours (it skips navigation on defaultPrevented).
// ⛔ NOT covered, accepted by the product (2026-09-27): navigation done by CODE —
// sign-out, the permissions-drift "sign in again", a forced return to the picker.
// ⛔ This is why the screen's tabs are BUTTONS, not links: a link tab would trip it.
import { useEffect } from 'react';

function isInternalNavigation(event) {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null; // new tab/window
  const a = event.target?.closest?.('a[href]');
  if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return null;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  const here = window.location.pathname + window.location.search;
  return url.pathname + url.search === here ? null : a;
}

export default function useLeaveGuard(dirty, message) {
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    const onClick = (e) => {
      if (!isInternalNavigation(e)) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(message)) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    // CAPTURE is load-bearing: React's delegated handlers sit on the render root, a descendant
    // of document, so this runs before <Link>'s and stopPropagation keeps it from navigating.
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, message]);
}
